// ui.bridge.ts — Bridge between vanilla game and React UI with throttled updates

import { dispatchNetworkMessage } from "../networking/network.dispatch";
import type { ToastData } from "./ui.types";

export class UIBridge {
  private toasts: ToastData[] = [];
  private toastTimeouts: Map<string, NodeJS.Timeout> = new Map();

  // Game state accessors (set from client.ts)
  private gameState: any = null;

  addToast(
    message: string,
    type: "info" | "success" | "warning" | "error" = "info",
    duration = 3000,
  ) {
    const toast: ToastData = {
      id: `toast-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      message,
      type,
      timeout: duration,
      createdAt: Date.now(),
    };

    this.toasts.push(toast);

    const timeoutId = setTimeout(() => {
      this.removeToast(toast.id);
    }, duration);

    this.toastTimeouts.set(toast.id, timeoutId);

    return toast.id;
  }

  removeToast(toastId: string) {
    const index = this.toasts.findIndex((t) => t.id === toastId);
    if (index >= 0) {
      this.toasts.splice(index, 1);

      const timeoutId = this.toastTimeouts.get(toastId);
      if (timeoutId) {
        clearTimeout(timeoutId);
        this.toastTimeouts.delete(toastId);
      }
    }
  }

  sendMessage(message: any) {
    const success = dispatchNetworkMessage(message);
    if (!success) {
      console.warn("Network command dispatch failed", message);
      this.addToast("Not connected to server", "error", 3000);
    }
  }

  showToast(
    message: string,
    type: "info" | "success" | "warning" | "error" = "info",
    duration = 3000,
  ) {
    return this.addToast(message, type, duration);
  }

  clearToasts() {
    this.toastTimeouts.forEach((timeoutId) => {
      clearTimeout(timeoutId);
    });
    this.toastTimeouts.clear();

    this.toasts = [];
  }

  getToasts() {
    return [...this.toasts];
  }

  setGameState(state: any) {
    this.gameState = state;
  }

  getGameModeState(): {
    mode: string;
    teamScores?: Record<number, number>;
    captureLimit?: number;
    flags?: Array<{
      id: string;
      teamId: number;
      state: "home" | "carried" | "dropped";
      carrierId?: string;
    }>;
  } | null {
    if (!this.gameState?.gameModeState) {
      return null;
    }

    const gms = this.gameState.gameModeState;
    return {
      mode: gms.mode,
      teamScores: gms.teamScores,
      captureLimit: gms.captureLimit,
      flags: gms.flags,
    };
  }

  isGameStarted(): boolean {
    return this.gameState?.started ?? false;
  }
}

export const uiBridge = new UIBridge();
